import { Box, BoxProps, ColorOptions, EmptyView, Form, Loader, ModBox, Spacer, Text } from '@jfs/ui-new'
import { Button } from '../../components/AdminButton'
import { useEffect, useRef, useState } from 'react'
import ActionsMenu from '../../components/ActionMenu/ActionsMenu'
import { ModalAlertView } from '../../components/ModalAlertView'
import { SearchFilter } from '../../components/SearchFilter'
import { useLazyApiData, useStores } from '../../data/store'
import { isAccessible } from '../../utils'
import { getSelectedDate } from '../../utils/constants'
import { formData } from './formData'

const FormBox = ({ children }: any) => {
  return <Box w={'100%'}>{children}</Box>
}

const PayoutCmsUpdate = () => {
  const [searchText, setSearchText] = useState('')
  const [showAlert, setShowAlert] = useState(false)
  const {
    update,
    accessConfig,
    searchSettlement,
    getSetlementDetails,
    updateSelectedDateInfo,
    selectedDateInfo,
    searchedSettlementData,
  } = useStores((store) => ({
    update: store.payoutCms.updateSettlement,
    accessConfig: store.user.getAccessConfig(['ess', 'payout_cms_update']),
    searchSettlement: store.payoutCms.searchSettlement,
    getSetlementDetails: store.payoutCms.getSetlementDetails,
    updateSelectedDateInfo: store.updateSelectedDateInfo,
    selectedDateInfo: store.selectedDateInfo,
    searchedSettlementData: store.payoutCms.settlement,
  }))
  const [date, setDate] = useState(new Date(getSetlementDetails()?.settlementDate ?? new Date()))
  const [updateStatusList, setUpdateStatusList] = useState([])
  const [errorDropdown, setErrorDropdown] = useState('')
  const valuesRef = useRef<any>({})

  const [lazySearchSettlement, { state: searchApiState, data: searchApiData }] = useLazyApiData(searchSettlement)
  const [lazyUpdateSettlement, { state: updateApiState }] = useLazyApiData(update)
  const handleSearch = () => {
    searchApiState !== 'pending' && lazySearchSettlement(searchText)
  }
  const renderActionButton = ({ values, isValid }) => {
    valuesRef.current = values

    const handleUpdateSettlement = () => {
      if (values?.status === 'PAYOUT_FAILED') {
        setShowAlert(true)
      } else {
        lazyUpdateSettlement(values, searchText)
      }
    }
    setDate(values?.settlementDate ?? new Date())
    return (
      <Button
        onPress={handleUpdateSettlement}
        height={36}
        isFull
        disabled={!(isValid && updateApiState !== 'pending' && isAccessible(accessConfig, 'update'))}>
        {updateApiState === 'pending' ? (
          <Loader color="WHITE" />
        ) : (
          <Text title={'Update'} color={'WHITE'} fontFamily={'jiotype-bold'} />
        )}
      </Button>
    )
  }

  useEffect(() => {
    updateSelectedDateInfo({
      fromDate: getSelectedDate(2),
      toDate: selectedDateInfo?.toDate ?? new Date(Date.now()),
      position: {
        top: selectedDateInfo?.position?.top ?? 0,
        left: selectedDateInfo?.position?.left ?? 0,
        visible: selectedDateInfo?.position?.visible ?? false,
      },
      dateLabel: 'Last 7 days',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (updateApiState === 'fulfilled') {
      lazySearchSettlement(searchText)
    }
  }, [updateApiState])

  useEffect(() => {
    setDate(new Date(getSetlementDetails()?.settlementDate ?? new Date()))
    if (searchApiState === 'fulfilled') {
      setErrorDropdown('')
      const status = searchedSettlementData?.status
      if (status) {
        if (status === 'PAYOUT_FILE_GENERATED') {
          setUpdateStatusList([
            {
              id: '1',
              value: 'PAYOUT_SUCCESS',
            },
            {
              id: '2',
              value: 'PAYOUT_FAILED',
            },
          ])
        } else if (status === 'PAYOUT_SUCCESS') {
          setUpdateStatusList([
            {
              id: '1',
              value: 'PAYOUT_FAILED',
            },
          ])
        } else if (status === 'PAYOUT_FAILED') {
          setUpdateStatusList([
            {
              id: '1',
              value: 'PAYOUT_SUCCESS',
            },
          ])
        } else if (status === 'PAYOUT_PENDING') {
          setUpdateStatusList([
            {
              id: '1',
              value: 'PAYOUT_FAILED',
            },
            {
              id: '2',
              value: 'PAYOUT_SUCCESS',
            },
          ])
        } else {
          setErrorDropdown('Invalid current state, update not allowed.')
        }
      }
    }
  }, [searchApiState])

  const initialValues = getSetlementDetails()
  const fetchDataOnChange = () => {
    if (searchText?.length > 0) {
      lazySearchSettlement(searchText)
    }
  }

  const handleUpdate = () => {
    lazyUpdateSettlement(valuesRef.current, searchText)
  }
  return (
    <Box {...styles.container}>
      <Text title={'Update CMS'} fontSize={28} fontWeight={'600'} color={'#5a5c69' as ColorOptions} />
      <Spacer direction={'vertical'} size={16} />
      {isAccessible(accessConfig, 'fetch') && (
        <Box direction="row" w={'100%'}>
          <Box marginTop={-16}>
            <ActionsMenu
              fetchDataOnChange={fetchDataOnChange}
              filterVisible={false}
              setFilterVisible={null}
              showSearch={false}
              enableFilter={false}
              showCalendar={true}
              defaultPage={1}
              type="updatepayoutcms"
              filterApplied={false}
              showPagination={false}
            />
          </Box>
          <Spacer size={32} direction="horizontal" />
          <SearchFilter
            onChange={setSearchText}
            onSearch={handleSearch}
            placeholder={'Settlement ID'}
            textWidth={200}
            isFilter={false}
          />
        </Box>
      )}
      <Spacer direction={'vertical'} size={16} />
      <ModBox
        f={1}
        align={'center'}
        justify={'center'}
        show={
          searchApiState === 'pending' ||
          searchApiState === 'rejected' ||
          (searchApiState === 'fulfilled' && searchApiData?.message === 'No record found')
        }>
        <EmptyView
          onRetry={handleSearch}
          isLoading={searchApiState === 'pending'}
          loaderComponent={<Loader type="small" color="PRIMARY" />}
          emptyMessage={searchApiData?.message ?? 'Error while processing'}
          error={
            searchApiData?.message &&
            searchApiData?.message !== 'No record found' && {
              networkError: {
                data: { errors: [{ details: searchApiData?.message }] },
              },
            }
          }
        />
      </ModBox>
      {isAccessible(accessConfig, 'update') && searchApiState === 'fulfilled' && (
        <Form
          formData={formData(date, true, updateStatusList, errorDropdown)}
          container={FormBox}
          actionButtons={renderActionButton}
          initialValues={initialValues}
        />
      )}
      <ModalAlertView
        message="Please double check the data, as it may lead to double payment"
        show={showAlert}
        showHeader={false}
        title={'Alert'}
        buttonTitle={'I have checked, proceed to update'}
        cancelTitle={"I'll Check Later"}
        onDismiss={() => setShowAlert(false)}
        onPress={() => handleUpdate()}
        onCancel={() => setShowAlert(false)}
        positiveButtonColor={'PRIMARY'}
        cancelButtonColor={'WHITE'}
        height={100}
      />
    </Box>
  )
}

const styles = {
  container: {
    h: '100%',
    bg: 'WHITE',
    shadowRadius: 8,
    shadowColor: '#0000001F' as ColorOptions,
    borderRadius: 8,
    p: 20,
    overflow: 'auto',
  } as BoxProps,
}

export default PayoutCmsUpdate
